import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import "./Home.css";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

/* ─── Data ─────────────────────────────────────────────── */
const productCategories = [
  {
    icon: "✨",
    iconClass: "kg-product-icon-purple",
    title: "Personal Care",
    desc: "Skin, hair, and hygiene essentials crafted for daily indulgence and confidence.",
  },
  {
    icon: "🏠",
    iconClass: "kg-product-icon-blue",
    title: "Home & Hygiene",
    desc: "Trusted cleaning and household solutions that keep your home fresh and safe.",
  },
  {
    icon: "🛒",
    iconClass: "kg-product-icon-amber",
    title: "Toilet Hygiene",
    desc: "Quality everyday staples and lifestyle products for the modern household.",
  },
];

const steps = [
  {
    num: "01",
    title: "Create Account",
    desc: "Sign up in minutes. No complicated process — just your basics.",
  },
  {
    num: "02",
    title: "Choose Products",
    desc: "Browse our wide FMCG catalog and pick what suits your life.",
  },
  {
    num: "03",
    title: "Build Your Network",
    desc: "Invite others, grow your circle, and start earning referral rewards.",
  },
  {
    num: "04",
    title: "Earn & Grow",
    desc: "Level up through our reward tiers and watch your income scale.",
  },
];

const stats = [
  { value: "50K+", label: "Active Members" },
  { value: "200+", label: "Products" },
  { value: "4", label: "Earning Levels" },
  { value: "99%", label: "Satisfaction" },
];

/* ─── Animated Counter ─────────────────────────────────── */
function AnimatedCounter({ value }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const num = parseInt(value.replace(/\D/g, ""), 10);
          const suffix = value.replace(/\d/g, "");
          let start = 0;
          const step = Math.ceil(num / (1200 / 16));
          const timer = setInterval(() => {
            start += step;
            if (start >= num) {
              setDisplay(value);
              clearInterval(timer);
            } else setDisplay(start + suffix);
          }, 16);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return <span ref={ref}>{display}</span>;
}

/* ─── Product Card ─────────────────────────────────────── */
function ProductCard({ product }) {
  const img =
    Array.isArray(product?.images) && product.images.length > 0
      ? product.images[0]
      : null;

  const discount =
    product.originalPrice && product.offeredPrice
      ? Math.round(
          ((product.originalPrice - product.offeredPrice) /
            product.originalPrice) *
            100,
        )
      : 0;

  return (
    <div className="kg-featured-card">
      {/* Image */}
      <div className="kg-featured-img-wrap bg-white">
        {img ? (
          <img
            src={img}
            alt={product.name}
            className="kg-featured-img object-fit-contain"
            loading="lazy"
          />
        ) : (
          <div className="kg-featured-img-placeholder">
            <i className="bi bi-box-seam" />
          </div>
        )}
        {product.stock <= 0 && (
          <span className="kg-featured-badge kg-featured-badge--oos">
            Out of Stock
          </span>
        )}
      </div>

      {/* Body */}
      <div className="kg-featured-body">
        <p className="kg-featured-category">{product.category || "Product"}</p>
        <h4 className="kg-featured-name">{product.name}</h4>
        <div className="kg-featured-price-row">
          <span className="kg-featured-price">₹{product.offeredPrice}</span>
          {product.originalPrice !== product.offeredPrice && (
            <span className="kg-featured-mrp">
              <del>₹{product.originalPrice}</del>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Component ────────────────────────────────────────── */
function Home() {
  const navigate = useNavigate();

  /* ── Featured products state ── */
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/Product/GetAllProductsWithFilter?page=1&limit=8`,
        );
        if (!res.ok) throw new Error();
        const result = await res.json();
        setFeaturedProducts(result?.data?.products || []);
      } catch {
        // silently fail — section just won't show
      } finally {
        setProductsLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="kg-page">
      {/* ── HERO ─────────────────────────────────────────── */}
      <section id="hero" className="kg-hero">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-12">
              <div className="kg-hero-content mb-lg-0 mb-8">
                <h1 className="kg-hero-title my-4">
                  Your Gateway to <em>Smart Shopping</em> & Smart Earning
                  &mdash; <span>Khayat Group</span>
                </h1>
                <p className="kg-hero-sub mb-4 lh-sm">
                  Khayat Group combines premium daily-use products with a
                  powerful reward-driven earning system — empowering you to
                  build a sustainable income while living better.
                </p>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className="btn-kg-hero-primary rounded-4"
                    onClick={() => navigate("/login")}
                  >
                    Login to Dashboard
                  </button>
                  <a
                    className="btn-kg-hero-secondary rounded-4"
                    href="/assets/khayatgroup.apk"
                    download={true}
                  >
                    Download App
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-12 d-lg-block d-none">
              <div className="d-flex justify-content-end align-items-center h-100">
                <img
                  src="/assets/images/icon.png"
                  width="200"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ABOUT ─────────────────────────────────────────── */}
      <section id="about" className="kg-about kg-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <p className="kg-section-eyebrow mb-1">Who We Are</p>
              <h2 className="kg-section-title mb-3">
                Built on Trust, Products &amp; People
              </h2>
              <p className="kg-section-sub mb-4 lh-sm">
                Khayat Group is a fast-growing FMCG-based network marketing
                company that blends daily essential products with a
                reward-driven earning system. We empower individuals to build
                sustainable income while enjoying premium-quality products.
              </p>
              <ul className="kg-about-list">
                <li>Premium quality across all product categories</li>
                <li>Transparent reward and level-up system</li>
                <li>Community-first approach to growth</li>
                <li>Backed by a vision of financial freedom for all</li>
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="kg-about-visual">
                <div className="kg-about-card">
                  <span className="kg-about-card-tag mb-3 d-inline-block ls-md px-5">
                    Our Mission
                  </span>
                  <h3 className="mb-2">
                    Everyday Products. Extraordinary Opportunity.
                  </h3>
                  <p className="lh-sm">
                    Turn your daily purchases into a pathway to financial
                    independence through our proven network model.
                  </p>
                </div>
                <div className="kg-about-accent">
                  <div className="num">4+</div>
                  <div className="lbl">Product Categories</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRODUCT CATEGORIES ────────────────────────────── */}
      <section id="products" className="kg-products kg-section">
        <div className="container">
          <p className="kg-section-eyebrow mb-1">What We Offer</p>
          <h2 className="kg-section-title mb-2">Our Product Range</h2>
          <p className="kg-section-sub mb-5 lh-sm">
            From wellness to home essentials — everything you need, every single
            day.
          </p>
          <div className="row g-2">
            {productCategories.map((p) => (
              <div className="col-12 col-md-6 col-lg-4" key={p.title}>
                <div className="kg-product-card">
                  <div className={`kg-product-icon mb-3 ${p.iconClass}`}>
                    {p.icon}
                  </div>
                  <h3 className="mb-2">{p.title}</h3>
                  <p className="lh-sm">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED PRODUCTS ─────────────────────────────── */}
      {(productsLoading || featuredProducts.length > 0) && (
        <section className="kg-featured-products kg-section">
          <div className="container">
            <p className="kg-section-eyebrow mb-1">Fresh from the Shelf</p>
            <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap gap-2">
              <h2 className="kg-section-title mb-0">Featured Products</h2>
              <button
                className="kg-featured-viewall"
                onClick={() => navigate("/products")}
              >
                View All Products <i className="bi bi-arrow-right ms-1" />
              </button>
            </div>

            {productsLoading ? (
              /* skeleton shimmer */
              <div className="row g-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div className="col-6 col-md-4 col-lg-3" key={i}>
                    <div className="kg-featured-skeleton">
                      <div className="kg-skeleton-img" />
                      <div className="kg-skeleton-line kg-skeleton-line--lg mt-3" />
                      <div className="kg-skeleton-line mt-2" />
                      <div className="kg-skeleton-line kg-skeleton-line--sm mt-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="row g-3">
                {featuredProducts.slice(0, 4).map((p) => (
                  <div className="col-6 col-md-4 col-lg-3" key={p.productId}>
                    <ProductCard product={p} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── FOUNDERS ──────────────────────────────────────── */}
      <section id="founder" className="kg-founder">
        <div className="container">
          <div className="text-center mb-5">
            <p className="kg-section-eyebrow mb-1">Meet the Founders</p>
            <h2 className="kg-section-title">The Trio Behind Khayat Group</h2>
            <p className="kg-founder-intro mx-auto mt-3 lh-sm">
              Three friends, one mission — to bring quality daily-use products
              and a real earning opportunity to every household in the
              community.
            </p>
          </div>
          <div className="d-flex justify-content-center">
            <div className="col-lg-8 col-md-10 col-12">
              <div className="row justify-content-center g-2">
                {[
                  {
                    src: "shafeek",
                    name: "Shafeek",
                    role: "Co-Founder",
                    init: "S",
                  },
                  {
                    src: "sadiq",
                    name: "Mohammad Sadiq",
                    role: "Co-Founder & CEO",
                    init: "MS",
                    featured: true,
                  },
                  {
                    src: "shabeeb",
                    name: "Shabeeb",
                    role: "Co-Founder",
                    init: "SB",
                  },
                ].map((f) => (
                  <div className="col-12 col-md-4" key={f.name}>
                    <div
                      className={`kg-founder-card${f.featured ? " kg-founder-card--featured" : ""}`}
                    >
                      <div className="kg-founder-photo-wrap">
                        <img
                          src={`/assets/images/founders/${f.src}.jpeg`}
                          alt={f.name}
                          className="kg-founder-photo"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        <div className="kg-founder-placeholder">{f.init}</div>
                      </div>
                      <div className="kg-founder-card-body text-center">
                        <h3 className="kg-founder-card-name">{f.name}</h3>
                        <p className="kg-founder-card-role">{f.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────── */}
      <section id="steps" className="kg-steps kg-section">
        <div className="container">
          <p className="kg-section-eyebrow text-center mb-1">How It Works</p>
          <h2 className="kg-section-title text-center mb-5">
            Your Journey in 4 Simple Steps
          </h2>
          <div className="row kg-steps-row g-4">
            {steps.map((s) => (
              <div className="col-6 col-lg-3" key={s.num}>
                <div className="kg-step">
                  <div className="kg-step-num">{s.num}</div>
                  <h3 className="mb-2">{s.title}</h3>
                  <p className="lh-sm">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="kg-cta text-center mt-2">
        <div className="container">
          <p className="kg-section-eyebrow mb-2">Join the Community</p>
          <h2 className="kg-cta-title mb-3">Ready to Start Your Journey?</h2>
          <p className="kg-cta-sub mx-auto mb-5 lh-sm">
            Join thousands of members already turning everyday purchases into a
            growing income stream.
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <button
              className="btn-kg-cta-primary rounded-4"
              onClick={() => navigate("/sign-up")}
            >
              Create Free Account
            </button>
            <button
              className="btn-kg-cta-secondary rounded-4"
              onClick={() => navigate("/login")}
            >
              Login to Dashboard
            </button>
          </div>
          <div className="d-flex flex-wrap justify-content-center gap-2 mt-2">
            <button
              className="btn-kg-cta-secondary rounded-4"
              onClick={() => navigate("/terms")}
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
